import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

type Sorteo = {
  id: number;
  nombre: string;
  premio: string;
  descripcion: string;
  marcas_bonificadas: { marca: string; entradas: number }[];
  marcas: string[];
  requiere_factura: boolean;
  formato_factura: 'ticobaterias' | 'electronica' | 'libre';
  exigir_factura_electronica: boolean;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  reglamento: string;
};

type ItemLista = {
  slug: string;
  nombre: string;
  premio: string;
  registro: 'abierto' | 'programado';
  requiere_factura: boolean;
  fecha_inicio: string | null;
  fecha_fin: string | null;
};

type Info = {
  abierto: boolean;
  programado?: boolean;
  cerrado?: boolean;
  ahora?: string;
  sorteo?: Sorteo;
  texto_consentimiento?: string;
  participantes?: number;
};

// Las fechas del backend vienen en UTC sin zona
const aFecha = (s: string) => new Date(/(Z|[+-]\d{2}:\d{2})$/.test(s) ? s : s + 'Z');

const fechaLarga = (s: string) =>
  aFecha(s).toLocaleString('es-CR', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' });

function tiempoRestante(ms: number) {
  if (ms <= 0) return '';
  const min = Math.floor(ms / 60000);
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  if (d > 0) return `${d} ${d === 1 ? 'día' : 'días'} ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min`;
  return `${Math.ceil(ms / 1000)} s`;
}

/** Cuenta regresiva con la hora del servidor (no depende del reloj del celular). */
function CuentaRegresiva({ hasta, desfase, alTerminar, texto }: { hasta: string; desfase: number; alTerminar: () => void; texto: string }) {
  const [ahora, setAhora] = useState(() => Date.now() + desfase);
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now() + desfase), 1000);
    return () => clearInterval(t);
  }, [desfase]);
  const restante = aFecha(hasta).getTime() - ahora;
  useEffect(() => {
    if (restante <= 0) alTerminar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restante <= 0]);
  if (restante <= 0) return null;
  return <div className="cuenta">{texto} <b>{tiempoRestante(restante)}</b></div>;
}
type Resultado = { entradas: number; bonificada: boolean; marca: string; participantes: number };

const PALABRA: Record<number, string> = { 2: 'DOBLE', 3: 'TRIPLE', 4: 'CUÁDRUPLE' };
const multiple = (n: number) => PALABRA[n] || `×${n}`;

const FORM_VACIO = {
  nombre: '',
  marcaSel: '',
  marcaOtra: '',
  factura: '',
  telefono: '',
  correo: '',
  sitio_web: '', // campo trampa para bots (oculto)
};

const CSS = `
.rifa { background: radial-gradient(120% 90% at 100% 100%, #52107f 0%, #3A008C 55%); color: #fff;
  min-height: 80vh; padding: 40px 20px 60px; display: flex; flex-direction: column; align-items: center; }
.rifa .logo { width: min(300px, 78vw); height: auto; margin-bottom: 12px; filter: drop-shadow(0 2px 8px rgba(0,0,0,.45)); }
.rifa .eyebrow { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 15px; letter-spacing: .06em; }
.rifa h1 { font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 30px; margin: 6px 0 4px; text-align: center; line-height: 1.1; }
.rifa .premio { color: #FFD100; font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 20px; text-align: center; }
.rifa .subt { color: #C9B8F0; font-size: 14px; text-align: center; margin: 8px 0 20px; max-width: 400px; line-height: 1.5; white-space: pre-line; }
.rifa .promo { width: 100%; max-width: 400px; margin: 0 0 16px; padding: 12px 14px; border: 1px dashed #FFD100;
  border-radius: 8px; background: rgba(255,209,0,.08); color: #FFD100; font-size: 14px; text-align: center; line-height: 1.5; }
.rifa .tarjeta { width: 100%; max-width: 400px; background: #431398; border: 1px solid #6A3DBA; border-radius: 12px; padding: 24px; }
.rifa .campo { margin-bottom: 14px; }
.rifa label { display: block; font-size: 13px; color: #C9B8F0; margin-bottom: 6px; }
.rifa input[type=text], .rifa input[type=email], .rifa input[type=tel], .rifa select {
  width: 100%; padding: 12px 14px; background: #2A0068; border: 1px solid #6A3DBA; border-radius: 8px;
  color: #fff; font-size: 16px; font-family: inherit; }
.rifa select option { background: #2A0068; color: #fff; }
.rifa input:focus, .rifa select:focus { outline: none; border-color: #FFD100; }
.rifa input::placeholder { color: #9c86d6; }
.rifa .con-error { border-color: #EC0000 !important; }
.rifa .error { color: #FFB3B3; font-size: 12px; margin-top: 4px; }
.rifa .ayuda { color: #9c86d6; font-size: 12px; margin-top: 4px; }
.rifa .aviso { margin: 0 0 14px; padding: 10px 12px; background: #4A0A0A; border: 1px solid #EC0000; border-radius: 8px; color: #FFD6D6; font-size: 13px; }
.rifa .condiciones { margin-top: 6px; padding: 12px 14px; background: rgba(0,0,0,.18); border: 1px solid #6A3DBA; border-radius: 8px;
  font-size: 12.5px; color: #E4DAFB; line-height: 1.55; }
.rifa .condiciones button { background: none; border: none; padding: 0; color: #FFD100; font-size: 12.5px; text-decoration: underline; cursor: pointer; }
.rifa .reglamento { margin-top: 10px; max-height: 260px; overflow-y: auto; white-space: pre-line; font-size: 12px; color: #C9B8F0;
  border-top: 1px solid #6A3DBA; padding-top: 10px; }
.rifa .boton { width: 100%; margin-top: 12px; padding: 14px; background: #EC0000; color: #fff; border: none; border-radius: 8px;
  font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 19px; letter-spacing: .04em; cursor: pointer; }
.rifa .boton:hover:not(:disabled) { background: #C40000; }
.rifa .boton:disabled { opacity: .6; cursor: default; }
.rifa .secundario { background: transparent; border: 1px solid #6A3DBA; font-size: 16px; }
.rifa .exito { text-align: center; }
.rifa .exito h2 { font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 26px; margin: 0 0 10px; color: #00E766; }
.rifa .exito.doble h2 { color: #FFD100; }
.rifa .exito p { color: #C9B8F0; font-size: 14px; line-height: 1.6; margin: 0; }
.rifa .contador { margin-top: 20px; font-size: 13px; color: #C9B8F0; }
.rifa .contador b { color: #FFD100; font-family: 'Barlow Condensed', sans-serif; font-size: 20px; }
.rifa .cuenta { margin: 0 0 16px; padding: 8px 16px; border-radius: 999px; background: rgba(255,255,255,.08); color: #C9B8F0; font-size: 14px; }
.rifa .cuenta b { color: #FFD100; font-family: 'Barlow Condensed', sans-serif; font-size: 18px; }
.rifa .programado { width: 100%; max-width: 400px; text-align: center; background: #431398; border: 1px solid #6A3DBA; border-radius: 12px; padding: 24px; }
.rifa .programado .cuando { color: #FFD100; font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 22px; margin: 6px 0 14px; text-transform: capitalize; }
.rifa .promo .fila { margin-top: 4px; }
.rifa .lista { width: 100%; max-width: 440px; display: flex; flex-direction: column; gap: 12px; margin-top: 18px; }
.rifa .item { display: block; text-decoration: none; color: #fff; background: #431398; border: 1px solid #6A3DBA; border-radius: 12px; padding: 16px 18px; }
.rifa .item:hover { border-color: #FFD100; }
.rifa .item .n { font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 22px; line-height: 1.1; }
.rifa .item .p { color: #FFD100; font-size: 14px; margin-top: 4px; }
.rifa .item .d { color: #C9B8F0; font-size: 12px; margin-top: 6px; }
.rifa .trampa { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
`;

/** ticobaterias.com/rifa: lista de sorteos abiertos (si hay uno solo, entra directo). */
function ListaRifas() {
  const navigate = useNavigate();
  const [lista, setLista] = useState<ItemLista[] | null>(null);

  useEffect(() => {
    document.title = 'Sorteos — TicoBaterías®';
    axios.get(`${API}/rifa/publico`)
      .then(r => {
        const sorteos: ItemLista[] = r.data.sorteos || [];
        if (sorteos.length === 1) navigate(`/rifa/${sorteos[0].slug}`, { replace: true });
        else setLista(sorteos);
      })
      .catch(() => setLista([]));
  }, [navigate]);

  return (
    <main className="rifa">
      <style>{CSS}</style>
      <img className="logo" src="/rifa-assets/ticobaterias-logo.webp" alt="TicoBaterías" />
      <div className="eyebrow">SORTEOS · PURA <span style={{ color: '#EC0000' }}>ENERGÍA</span> ⚡</div>
      {lista === null && <p className="subt">Cargando...</p>}
      {lista && lista.length === 0 && (
        <>
          <h1>No hay un sorteo abierto ahora</h1>
          <p className="subt">Síguenos en redes para enterarte del próximo sorteo, o revisa nuestro catálogo de baterías.</p>
          <a href="/catalogo" className="boton" style={{ maxWidth: 400, textAlign: 'center', textDecoration: 'none' }}>Ver catálogo</a>
        </>
      )}
      {lista && lista.length > 1 && (
        <>
          <h1>Elige tu sorteo</h1>
          <div className="lista">
            {lista.map(x => (
              <Link key={x.slug} to={`/rifa/${x.slug}`} className="item">
                <div className="n">{x.requiere_factura ? '🧾' : '🎉'} {x.nombre}</div>
                {x.premio && <div className="p">🎁 {x.premio}</div>}
                <div className="d">
                  {x.registro === 'programado' && x.fecha_inicio
                    ? `Abre el ${fechaLarga(x.fecha_inicio)}`
                    : x.fecha_fin ? `Registro abierto hasta el ${fechaLarga(x.fecha_fin)}` : 'Registro abierto'}
                  {x.requiere_factura ? ' · con tu factura de compra' : ' · abierto a todo público'}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </main>
  );
}

export default function RifaRegistro() {
  const { slug } = useParams<{ slug?: string }>();
  return slug ? <FormularioRifa slug={slug} /> : <ListaRifas />;
}

/** ticobaterias.com/rifa/<slug>: formulario de un sorteo. */
function FormularioRifa({ slug }: { slug: string }) {
  const [info, setInfo] = useState<Info | null>(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [desfase, setDesfase] = useState(0); // diferencia entre el reloj del servidor y el del celular
  const [verReglamento, setVerReglamento] = useState(false);
  const [noExiste, setNoExiste] = useState(false);

  const cargarInfo = () =>
    axios.get(`${API}/rifa/publico/${encodeURIComponent(slug)}`)
      .then(r => {
        setInfo(r.data);
        setNoExiste(false);
        if (r.data.ahora) setDesfase(aFecha(r.data.ahora).getTime() - Date.now());
        document.title = `${r.data.sorteo?.nombre || 'Sorteo'} — TicoBaterías®`;
      })
      .catch(e => {
        if (e?.response?.status === 404) setNoExiste(true);
        setInfo({ abierto: false });
      });

  useEffect(() => {
    setInfo(null);
    setResultado(null);
    cargarInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const s = info?.sorteo;
  const bonos = s?.marcas_bonificadas || [];
  // Marcas del catalogo que no son patrocinadoras (para el resto del desplegable)
  const otrasMarcas = (s?.marcas || []).filter(m => !bonos.some(b => b.marca.toLowerCase() === m.toLowerCase()));
  const hayDesplegable = bonos.length > 0 || otrasMarcas.length > 0;
  // Patrocinadoras agrupadas por participaciones, de mayor a menor: {3: ['LTH'], 2: ['Bosch', 'Gonher']}
  const gruposPromo = Array.from(new Set(bonos.map(b => b.entradas)))
    .sort((a, b) => b - a)
    .map(n => ({ n, marcas: bonos.filter(b => b.entradas === n).map(b => b.marca) }));
  const unir = (l: string[]) => (l.length > 1 ? `${l.slice(0, -1).join(', ')} o ${l[l.length - 1]}` : l[0]);

  const cambiar = (campo: keyof typeof FORM_VACIO, valor: string) => {
    setForm(f => ({ ...f, [campo]: valor }));
    const clave = campo === 'marcaSel' || campo === 'marcaOtra' ? 'marca' : campo;
    setErrores(e => {
      const resto = { ...e };
      delete resto[clave];
      return resto;
    });
  };

  // Valores del desplegable: "M:<marca>" (del catalogo), "OTRA" (escribir) o "" (no sé)
  const marcaFinal = () => {
    if (!hayDesplegable) return form.marcaOtra;
    if (form.marcaSel.startsWith('M:')) return form.marcaSel.slice(2);
    if (form.marcaSel === 'OTRA') return form.marcaOtra;
    return '';
  };
  const conFactura = s?.requiere_factura !== false;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!s) return;
    setEnviando(true);
    setAviso('');
    setErrores({});
    try {
      const r = await axios.post(`${API}/rifa/registro`, {
        sorteo_id: s.id,
        nombre: form.nombre,
        marca: marcaFinal(),
        factura: conFactura ? form.factura : '',
        telefono: form.telefono,
        correo: form.correo,
        consentimiento: true, // presionar "Acepto y participo" es la aceptacion
        sitio_web: form.sitio_web,
      });
      setResultado(r.data);
      setInfo(i => (i ? { ...i, participantes: r.data.participantes ?? i.participantes } : i));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      const detalle = err?.response?.data?.detail;
      if (detalle && typeof detalle === 'object' && !Array.isArray(detalle)) {
        setErrores(detalle.errores || {});
        setAviso(detalle.mensaje || 'No pudimos guardar tu registro.');
      } else {
        setAviso('No pudimos guardar tu registro en este momento. Avísale al equipo del evento.');
      }
    } finally {
      setEnviando(false);
    }
  };

  const otraFactura = () => {
    setForm(f => ({ ...FORM_VACIO, nombre: f.nombre, telefono: f.telefono, correo: f.correo }));
    setResultado(null);
  };

  const claseInput = (campo: string) => (errores[campo] ? 'con-error' : '');
  const error = (campo: string) => errores[campo] && <div className="error">{errores[campo]}</div>;

  return (
    <main className="rifa">
      <style>{CSS}</style>
      <img className="logo" src="/rifa-assets/ticobaterias-logo.webp" alt="TicoBaterías" />
      <div className="eyebrow">SORTEO · PURA <span style={{ color: '#EC0000' }}>ENERGÍA</span> ⚡</div>

      {info === null && <p className="subt">Cargando...</p>}

      {s && info?.programado && (
        <>
          <h1>{s.nombre}</h1>
          {s.premio && <div className="premio">🎁 {s.premio}</div>}
          {s.descripcion && <p className="subt">{s.descripcion}</p>}
          <div className="programado" style={{ marginTop: 16 }}>
            <div style={{ color: '#C9B8F0', fontSize: 14 }}>El registro abre el</div>
            {s.fecha_inicio && <div className="cuando">{fechaLarga(s.fecha_inicio)}</div>}
            {s.fecha_inicio && (
              <CuentaRegresiva hasta={s.fecha_inicio} desfase={desfase} alTerminar={cargarInfo} texto="Faltan" />
            )}
            <p className="subt" style={{ margin: 0 }}>Guarda este enlace y vuelve a esa hora para registrarte.</p>
          </div>
        </>
      )}

      {info && !info.abierto && !info.programado && (
        <>
          <h1>{noExiste ? 'Este sorteo no existe' : s ? `${s.nombre}: el registro ya cerró` : 'Este sorteo no está disponible'}</h1>
          <p className="subt">
            {noExiste
              ? 'Revisa que el enlace esté bien escrito.'
              : 'Gracias por tu interés. Mira si hay otro sorteo abierto o revisa nuestro catálogo de baterías.'}
          </p>
          <Link to="/rifa" className="boton" style={{ maxWidth: 400, textAlign: 'center', textDecoration: 'none' }}>Ver sorteos abiertos</Link>
          <a href="/catalogo" className="boton secundario" style={{ maxWidth: 400, textAlign: 'center', textDecoration: 'none' }}>Ver catálogo</a>
        </>
      )}

      {s && info?.abierto && resultado && (
        <>
          <h1>¡Ya quedaste registrado!</h1>
          <div className={`tarjeta exito ${resultado.bonificada ? 'doble' : ''}`} style={{ marginTop: 16 }}>
            {resultado.bonificada ? (
              <>
                <h2>⭐ ¡Participación {multiple(resultado.entradas)}!</h2>
                <p>Tu batería es <b>{resultado.marca}</b>, así que entraste <b>{resultado.entradas} veces</b> a la ruleta. Espera el momento del sorteo, no necesitas hacer nada más.</p>
              </>
            ) : (
              <>
                <h2>✅ Listo</h2>
                <p>Ya estás en la ruleta. Espera el momento del sorteo para ver si ganaste, no necesitas hacer nada más.</p>
              </>
            )}
            <p style={{ marginTop: 12, fontSize: 12 }}>
              {conFactura
                ? 'Si ganas, te pediremos la factura original para entregarte el premio.'
                : 'Si ganas, te pediremos tu cédula para entregarte el premio.'}
            </p>
            {conFactura && <button type="button" className="boton secundario" onClick={otraFactura}>Registrar otra factura</button>}
          </div>
        </>
      )}

      {s && info?.abierto && !resultado && (
        <>
          <h1>{s.nombre}</h1>
          {s.premio && <div className="premio">🎁 {s.premio}</div>}
          <p className="subt">{s.descripcion || 'Llena tus datos para entrar a la ruleta. Es gratis y toma un minuto.'}</p>
          {s.fecha_fin && (
            <CuentaRegresiva hasta={s.fecha_fin} desfase={desfase} alTerminar={cargarInfo} texto="⏳ El registro cierra en" />
          )}

          {gruposPromo.length > 0 && (
            <div className="promo">
              🔋 <b>¡Tu marca de batería suma oportunidades!</b>
              {gruposPromo.map(g => (
                <div key={g.n} className="fila">
                  <b>{unir(g.marcas)}</b>: ¡participas <b>{multiple(g.n)}</b>!
                </div>
              ))}
            </div>
          )}

          <form className="tarjeta" onSubmit={enviar} noValidate autoComplete="on">
            {aviso && <div className="aviso">{aviso}</div>}

            <div className="trampa" aria-hidden="true">
              <label htmlFor="sitio_web">No llenar</label>
              <input type="text" id="sitio_web" tabIndex={-1} autoComplete="off" value={form.sitio_web} onChange={e => cambiar('sitio_web', e.target.value)} />
            </div>

            <div className="campo">
              <label htmlFor="nombre">Nombre completo</label>
              <input type="text" id="nombre" maxLength={100} placeholder="Ej. Juan Pérez" autoComplete="name"
                className={claseInput('nombre')} value={form.nombre} onChange={e => cambiar('nombre', e.target.value)} />
              {error('nombre')}
            </div>

            <div className="campo">
              <label htmlFor="marca">{conFactura ? 'Marca de la batería' : 'Marca de la batería de tu vehículo (opcional)'}</label>
              {hayDesplegable ? (
                <select id="marca" className={claseInput('marca')} value={form.marcaSel} onChange={e => cambiar('marcaSel', e.target.value)}>
                  <option value="">{conFactura ? 'Selecciona la marca…' : 'No sé / prefiero no decir'}</option>
                  {bonos.length > 0 && (
                    <optgroup label="⭐ Marcas patrocinadoras">
                      {[...bonos].sort((a, b) => b.entradas - a.entradas).map(b => (
                        <option key={b.marca} value={`M:${b.marca}`}>{b.marca} — ¡participas {multiple(b.entradas).toLowerCase()}!</option>
                      ))}
                    </optgroup>
                  )}
                  {otrasMarcas.length > 0 && (
                    <optgroup label={bonos.length > 0 ? 'Otras marcas' : 'Marcas'}>
                      {otrasMarcas.map(m => <option key={m} value={`M:${m}`}>{m}</option>)}
                    </optgroup>
                  )}
                  <option value="OTRA">Otra marca (escribirla)</option>
                </select>
              ) : (
                <input type="text" id="marca" maxLength={50} placeholder={conFactura ? 'Escribe la marca' : 'Ej. Bosch (si no sabes, déjalo vacío)'} className={claseInput('marca')}
                  value={form.marcaOtra} onChange={e => cambiar('marcaOtra', e.target.value)} />
              )}
              {hayDesplegable && form.marcaSel === 'OTRA' && (
                <input type="text" maxLength={50} placeholder="¿Cuál marca?" aria-label="Otra marca" style={{ marginTop: 8 }}
                  className={claseInput('marca')} value={form.marcaOtra} onChange={e => cambiar('marcaOtra', e.target.value)} />
              )}
              {error('marca')}
            </div>

            {conFactura && <div className="campo">
              <label htmlFor="factura">Número de factura</label>
              <input type="text" id="factura" maxLength={60} autoComplete="off" spellCheck={false}
                inputMode={s.formato_factura === 'electronica' ? 'numeric' : 'text'}
                autoCapitalize={s.formato_factura === 'ticobaterias' ? 'characters' : 'off'}
                placeholder={s.formato_factura === 'ticobaterias' ? 'Ej. DES-14505' : s.formato_factura === 'electronica' ? 'Ej. 00100001010000014505' : 'Número de tu factura'}
                className={claseInput('factura')} value={form.factura} onChange={e => cambiar('factura', e.target.value)} />
              {error('factura') || (
                <div className="ayuda">
                  {s.formato_factura === 'ticobaterias'
                    ? 'Está abajo a la derecha de tu factura, junto a «Factura Contado No.».'
                    : s.formato_factura === 'electronica'
                      ? 'Los 20 dígitos de «N. Factura Electrónica», arriba a la derecha de tu factura.'
                      : 'Cada factura participa una sola vez.'}
                </div>
              )}
            </div>}

            <div className="campo">
              <label htmlFor="telefono">Teléfono</label>
              <input type="tel" id="telefono" maxLength={20} inputMode="tel" placeholder="Ej. 8888 8888" autoComplete="tel"
                className={claseInput('telefono')} value={form.telefono} onChange={e => cambiar('telefono', e.target.value)} />
              {error('telefono')}
            </div>

            <div className="campo">
              <label htmlFor="correo">Correo electrónico</label>
              <input type="email" id="correo" maxLength={120} placeholder="Ej. nombre@correo.com" autoComplete="email"
                className={claseInput('correo')} value={form.correo} onChange={e => cambiar('correo', e.target.value)} />
              {error('correo')}
            </div>

            <div className="condiciones">
              {info?.texto_consentimiento}
              {s.reglamento && (
                <>
                  {' '}
                  <button type="button" onClick={() => setVerReglamento(v => !v)}>
                    {verReglamento ? 'Ocultar las condiciones del sorteo' : 'Ver las condiciones del sorteo'}
                  </button>
                  {verReglamento && <div className="reglamento">{s.reglamento}</div>}
                </>
              )}
            </div>
            {error('consentimiento')}

            <button type="submit" className="boton" disabled={enviando}>{enviando ? 'Registrando...' : 'Acepto y participo'}</button>
          </form>
        </>
      )}

      {info?.abierto && (
        <div className="contador">Ya somos <b>{info.participantes ?? 0}</b> participantes</div>
      )}
    </main>
  );
}
